import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Download, MessageSquare, Users } from 'lucide-react';

const RoomLogsPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [roomData, setRoomData] = useState(null);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    loadRoomLogs();
  }, [roomId]);

  const loadRoomLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/room-logs/${roomId}`);
      setRoomData(response.data.room);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Failed to load room logs');
    } finally {
      setLoading(false);
    }
  };

  const exportAsText = () => {
    let content = `Room: ${roomData.name}\n`;
    content += `Type: ${roomData.type}\n`;
    content += `Created: ${new Date(roomData.created_at).toLocaleString()}\n`;
    content += `Total Messages: ${messages.length}\n\n`;
    content += '='.repeat(50) + '\n\n';

    messages.forEach((msg) => {
      const timestamp = new Date(msg.created_at).toLocaleString();
      const sender = msg.sender?.username || 'Unknown';
      content += `[${timestamp}] ${sender}:\n${msg.content}\n\n`;
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${roomData.name.replace(/\s+/g, '_')}_logs.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Logs exported successfully');
  };

  const exportAsCSV = () => {
    let content = 'Timestamp,Username,Message\n';
    
    messages.forEach((msg) => {
      const timestamp = new Date(msg.created_at).toLocaleString();
      const sender = msg.sender?.username || 'Unknown';
      const message = msg.content.replace(/"/g, '""'); // Escape quotes
      content += `"${timestamp}","${sender}","${message}"\n`;
    });

    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${roomData.name.replace(/\s+/g, '_')}_logs.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Logs exported as CSV');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!roomData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Room not found</p>
          <Button onClick={() => navigate('/super-admin-dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <MessageSquare className="w-8 h-8" />
              Room Chat Logs
            </h1>
            <p className="text-gray-600">Viewing logs for: {roomData.name}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/super-admin-dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        {/* Room Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Room Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Room Name</p>
                <p className="font-semibold">{roomData.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Type</p>
                <Badge variant={roomData.type === 'public' ? 'default' : 'secondary'}>
                  {roomData.type}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">Created</p>
                <p className="text-sm">{new Date(roomData.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Messages</p>
                <p className="font-semibold text-blue-600">{messages.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export Buttons */}
        <div className="flex gap-2 mb-6">
          <Button onClick={exportAsText} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export as Text
          </Button>
          <Button onClick={exportAsCSV} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export as CSV
          </Button>
        </div>

        {/* Messages Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Chat Messages ({messages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <div className="space-y-3">
                {messages.map((msg, idx) => (
                  <div key={msg.id || idx} className="p-3 border rounded-lg bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800">
                          {msg.sender?.username || 'Unknown'}
                        </span>
                        {msg.sender?.role && (
                          <Badge variant="outline" className="text-xs">
                            {msg.sender.role}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(msg.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700">{msg.content}</p>
                    {msg.type === 'system' && (
                      <Badge variant="secondary" className="mt-2">System Message</Badge>
                    )}
                  </div>
                ))}
                {messages.length === 0 && (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-500">No messages in this room yet</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RoomLogsPage;
